import { useLocation, useNavigate } from "react-router-dom";
//#region types
import { DateRange } from "models/formTypes/util";
import { RouteConstants } from "utils/constants";
//#endregion
import { DateHelper, LocalizationHelperInstance, UrlHelper } from "utils/helpers";

const usePeriodSwitch = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const openPeriod = (path: string, date: Date, employeeId?: number) => {
    // Keeps the employee from the current URL; without one, both views fall back to the logged-in user.
    const queryParams = new URLSearchParams(location.search);
    if (employeeId !== undefined) {
      queryParams.set(RouteConstants.TimeReportingQueryParams.EmployeeId, `${employeeId}`);
    }
    queryParams.set(
      RouteConstants.TimeReportingQueryParams.CurrentDate,
      LocalizationHelperInstance.getFormattedDate(date)
    );
    navigate(UrlHelper.formatPath(`${path}?${queryParams.toString()}`), {
      replace: true,
      state: { background: location },
    });
  };

  // A week belongs to the month that holds its Monday, the rule the week view's "Lås månad" already follows. A week
  // that straddles two months then maps to exactly one of them, so week → month → week and month → week → month both
  // land where they started.
  const mondayOf = (date: Date) => DateHelper.getFirstDayOfWeek(date);

  const showMonth = (weekDate: Date, employeeId?: number) =>
    openPeriod("/TimeReport/Monthly", mondayOf(weekDate), employeeId);

  // Lands on the week the month was opened from, so the toggle can be undone; failing that this week; else the
  // month's first week. The month's arrows don't update the URL, so its date only counts while its week is in the
  // month.
  const showWeek = (month: DateRange) => {
    const openedFrom = new URLSearchParams(location.search).get(RouteConstants.TimeReportingQueryParams.CurrentDate);
    // The month's first week holds the first Monday on or after the 1st.
    const firstWeek = month.start && mondayOf(DateHelper.addDays(month.start, 6));
    const monday =
      [openedFrom ? DateHelper.parseLocalDate(openedFrom) : undefined, DateHelper.today()]
        .map((candidate) => candidate && mondayOf(candidate))
        .find((candidate) => candidate && DateHelper.withinIntervall(candidate, month.start, month.end)) ??
      firstWeek ??
      DateHelper.today();
    openPeriod("/TimeReport/Weekly", monday);
  };

  return { showWeek, showMonth };
};

export default usePeriodSwitch;
